// eslint-disable-next-line import/no-cycle
import { customersApi, samplesApi, locationsManagementApi } from '@/api';
import {
  LAB_SAMPLE_STATUSES,
  sampleDescriptionsList,
  sectorsList,
  laboratories,
} from '@/config/constants';
import { filterOptions } from '@/config/enumOptions';

const availableLaboratories = [
  { value: laboratories.CHIM, label: 'enums.laboratories.CHIM' },
  { value: laboratories.MICR, label: 'enums.laboratories.MICR' },
];

const availableStatuses = [
  {
    value: LAB_SAMPLE_STATUSES.ACCEPTED,
    label: 'enums.sampleStatuses.accepted',
  },
  {
    value: LAB_SAMPLE_STATUSES.INPROGRESS,
    label: 'enums.sampleStatuses.inprogress',
  },
  {
    value: LAB_SAMPLE_STATUSES.SUSPENDED,
    label: 'enums.sampleStatuses.suspended',
  },
  {
    value: LAB_SAMPLE_STATUSES.TOBEVERIFIED,
    label: 'enums.sampleStatuses.toBeVerified',
  },
  {
    value: LAB_SAMPLE_STATUSES.CANCELED,
    label: 'enums.sampleStatuses.canceled',
  },
  {
    value: LAB_SAMPLE_STATUSES.COMPLETED,
    label: 'enums.sampleStatuses.completed',
  },
  {
    value: LAB_SAMPLE_STATUSES.TOBEAPPROVED,
    label: 'enums.sampleStatuses.toBeApproved',
  },
];

const analysesStatusFilters = [
  {
    name: 'sampleId',
    label: 'customerSampleId',
    type: 'number',
    whereOperator: '=',
  },
  {
    name: 'id',
    label: 'sampleId',
    type: 'autocomplete',
    whereOperator: '=',
    placeholder: 'Digita almeno 3 caratteri per cercare...',
    autocompleteSearch: async (queryString, cb) => {
      // Require minimum 3 characters to prevent overly broad searches
      if (!queryString || queryString.trim().length < 3) {
        cb([
          {
            label: 'Digita almeno 3 caratteri per iniziare la ricerca',
            value: null,
            disabled: true,
          },
        ]);
        return;
      }
      const customersResponse = await samplesApi.autocompleteFetch(
        'labSampleCode',
        queryString,
        false
      );
      const filteredResults = customersResponse.data.map((c) => ({
        label: c.labSampleCode,
        value: c.id,
      }));
      cb(filteredResults);
    },
  },
  {
    name: 'customerProductName',
    label: 'customerSampleCode',
    type: 'text',
    whereOperator: 'regexp',
  },
  {
    name: 'year',
    label: 'year',
    type: 'number',
    min: '20',
    max: '99',
    whereOperator: '=',
  },
  {
    name: 'customerId',
    label: 'customerIdOrName',
    type: 'autocomplete',
    whereOperator: '=',
    autocompleteSearch: async (queryString, cb) => {
      const customersResponse = await customersApi.autocompleteFetch(
        'name',
        queryString,
        false,
        true
      );
      const filteredResults = customersResponse.data.map((c) => ({
        label: `${c.id} ${c.name}`,
        value: c.id,
      }));
      cb(filteredResults);
    },
  },
  {
    name: 'finalCustomer',
    label: 'finalCustomer',
    type: 'autocomplete',
    whereOperator: '=',
    placeholder: 'Digita almeno 3 caratteri per cercare...',
    autocompleteSearch: async (queryString, cb) => {
      if (!queryString || queryString.trim().length < 3) {
        cb([]);
        return;
      }
      try {
        const samplesResponse = await samplesApi.autocompleteFetch(
          'finalCustomer',
          queryString
        );
        const uniqueFinalCustomers = [
          ...new Set(
            samplesResponse.data.map((s) => s.finalCustomer).filter(Boolean)
          ),
        ];
        const filteredResults = uniqueFinalCustomers.map((finalCustomer) => ({
          label: finalCustomer,
          value: finalCustomer,
        }));
        cb(filteredResults);
      } catch (error) {
        cb([]);
      }
    },
  },
  {
    name: 'groupingId',
    label: 'grouping',
    type: 'text',
    whereOperator: '=',
  },
  {
    name: 'acceptedAt',
    label: 'receivedDate',
    type: 'dateDay',
  },
  {
    name: 'quarantineDate',
    label: 'quarantineDate',
    type: 'dateDay',
  },
  {
    name: 'sampleAnalysisStart',
    label: 'sampleAnalysisStart',
    type: 'dateDay',
  },
  {
    name: 'sampleAnalysisEnd',
    label: 'sampleAnalysisEnd',
    type: 'dateDay',
  },
  {
    name: 'productCode',
    label: 'productCode',
    type: 'text',
    whereOperator: 'regexp',
  },
  {
    name: 'productName',
    label: 'productName',
    type: 'text',
    whereOperator: 'regexp',
  },
  {
    name: 'sampleDescription',
    label: 'sampleDescription',
    type: 'select',
    filterable: true,
    whereOperator: '=',
    options: filterOptions(sampleDescriptionsList, 'enums.sampleDescriptions'),
  },
  {
    name: 'lot',
    label: 'lot',
    type: 'text',
    whereOperator: 'regexp',
  },
  {
    name: 'sector',
    label: 'sector',
    type: 'select',
    whereOperator: '=',
    options: filterOptions(sectorsList, 'enums.sectors'),
  },
  {
    name: 'laboratory',
    label: 'department',
    type: 'select',
    whereOperator: '=',
    options: availableLaboratories,
  },
  {
    name: 'locationId',
    label: 'locationIdOrName',
    type: 'autocomplete',
    whereOperator: '=',
    autocompleteSearch: async (queryString, cb) => {
      const locationsResponse = await locationsManagementApi.autocompleteFetch(
        'name',
        queryString,
        false,
        true
      );
      const filteredResults = locationsResponse.data.map((c) => ({
        label: c.name,
        value: c.id,
      }));
      cb(filteredResults);
    },
  },
  {
    name: 'status',
    label: 'status',
    type: 'select',
    whereOperator: '=',
    options: availableStatuses,
  },
  {
    name: 'progressive',
    label: 'progressive',
    type: 'text',
    whereOperator: 'regexp',
    customizeQueryString: (queryString) => queryString.padStart(5, '0'),
  },
  {
    name: 'orderId',
    label: 'orderId',
    type: 'text',
    whereOperator: '=',
  },
  {
    name: 'expectedDeliveryDate',
    label: 'expectedDeliveryDate',
    type: 'dateDay',
  },
  {
    name: 'stabilityTimepointToken',
    label: 'stabilityToken',
    type: 'text',
    whereOperator: 'regexp',
  },
  {
    name: 'isStability',
    label: 'stabilityFilter',
    type: 'stability',
    whereOperator: '=',
  },
];

export default analysesStatusFilters;
